/**
 * w.js — тонкий лоадер (docs/03 §3.4).
 *
 * Код вставки на сайте клиента:
 *   <script src="https://cdn.example.com/widget/v1/w.js" data-key="pk_live_…" async></script>
 *
 * Задачи лоадера: прочитать data-*, создать глобальную очередь, узнать имя
 * хэшированного бандла из manifest.json и подключить его. Ничего тяжёлого
 * здесь быть не должно — он кэшируется всего на 5 минут.
 */
import type { AIChatGlobal, WidgetBootOptions } from './types.js'

function currentScript(): HTMLScriptElement | null {
  if (document.currentScript instanceof HTMLScriptElement) return document.currentScript
  // Фоллбэк для старых браузеров и для случая, когда скрипт вставлен динамически.
  const scripts = document.getElementsByTagName('script')
  for (let i = scripts.length - 1; i >= 0; i--) {
    const s = scripts[i]
    if (s && s.src && /\/w\.js(\?|$)/.test(s.src)) return s
  }
  return null
}

function readOptions(script: HTMLScriptElement | null): WidgetBootOptions | null {
  if (!script) return null
  const key = script.getAttribute('data-key')
  if (!key) {
    // Без ключа работать не с чем. Молчим в консоли на проде, но в явной
    // ошибке больше пользы, чем в молчании: владелец сайта сразу увидит.
    console.warn('[AIChat] не задан data-key в теге подключения')
    return null
  }
  let scriptUrl: URL
  try {
    scriptUrl = new URL(script.src, location.href)
  } catch {
    scriptUrl = new URL(location.href)
  }

  // Каталог, в котором лежит сам w.js: оттуда же берём manifest.json и бандл.
  const assetBase = scriptUrl.href.replace(/\/[^/]*$/, '')
  const host = (script.getAttribute('data-host') || scriptUrl.origin).replace(/\/+$/, '')

  return {
    key,
    host,
    assetBase,
    silent: script.getAttribute('data-silent') === 'true',
    lang: script.getAttribute('data-lang') || undefined,
  }
}

function ensureGlobal(): AIChatGlobal {
  if (window.AIChat) return window.AIChat
  const queue: Array<[string, ...unknown[]]> = []
  const api = function (method: string, ...args: unknown[]) {
    queue.push([method, ...args])
  } as AIChatGlobal
  Object.defineProperty(api, 'q', { value: queue, enumerable: true })
  window.AIChat = api
  return api
}

function boot() {
  // Shadow DOM обязателен: без него виджет протечёт стилями на страницу.
  // На старых WebView тихо отказываемся — iframe-режим появится позже (docs/10).
  if (typeof Element === 'undefined' || !Element.prototype.attachShadow) return

  const script = currentScript()
  const options = readOptions(script)
  if (!options) return

  const api = ensureGlobal()
  if (api.options) return // уже загружались
  api.options = options

  const base = options.assetBase

  fetch(`${base}/manifest.json`, { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`manifest ${r.status}`))))
    .then((manifest: { bundle?: string }) => {
      if (!manifest.bundle) throw new Error('в manifest.json нет bundle')
      const el = document.createElement('script')
      el.src = `${base}/${manifest.bundle}`
      el.async = true
      el.crossOrigin = 'anonymous'
      el.onerror = () => console.warn('[AIChat] не удалось загрузить бандл виджета')
      document.head.appendChild(el)
    })
    .catch((err: unknown) => {
      console.warn('[AIChat] виджет не загрузился:', err)
    })
}

boot()
